import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, useTheme, Autocomplete, TextField, InputAdornment, FormControlLabel, Checkbox
} from '@mui/material';
import {
  Refresh as RefreshIcon, LocalShipping, Search as SearchIcon,
  FileDownload as ExcelIcon, PictureAsPdf as PdfIcon, Print as PrintIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { warehousesService } from '../../services/warehouses.service';
import { Warehouse } from '../../types/warehouses.types';
import axiosInstance from '../../services/axiosInstance';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fmtUnitPrice } from '../../utils/formatNumber';

interface DispatchItem {
  itemId: number; itemCode: string; itemName: string; itemNameAr: string;
  unitName: string; unitNameAr: string;
  totalQuantity: number; postedQty: number; draftQty: number;
  avgUnitCost: number; totalValue: number;
}
interface BranchGroup {
  branchId: number; branchName: string; branchNameAr: string;
  items: DispatchItem[];
  branchTotalQuantity: number; branchTotalValue: number;
  branchPostedQty: number; branchDraftQty: number;
}
interface ReportResult {
  generatedAt: string;
  warehouseId?: number; warehouseName: string; warehouseNameAr: string;
  fromDate?: string; toDate?: string;
  branches: BranchGroup[];
  grandTotalQuantity: number; grandTotalValue: number;
  totalBranches: number; totalUniqueItems: number;
}

const WarehouseDispatchReport: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const { minDate, maxDate } = useFiscalDateRange();
  const [whFilter, setWhFilter] = useState<number | ''>('');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeDraft, setIncludeDraft] = useState(true);
  const [includePosted, setIncludePosted] = useState(true);

  useEffect(() => {
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
    warehousesService.getAll().then(setWarehouses).catch(() => {});
  }, []);

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (whFilter) params.append('warehouseId', whFilter.toString());
      if (fromDate) params.append('fromDate', `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, '0')}-${String(fromDate.getDate()).padStart(2, '0')}`);
      if (toDate) params.append('toDate', `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, '0')}-${String(toDate.getDate()).padStart(2, '0')}`);
      params.append('includeDraft', includeDraft.toString());
      params.append('includePosted', includePosted.toString());
      const { data } = await axiosInstance.get(`/WarehouseDispatchReport?${params.toString()}`);
      setResult(data);
      toast.success(isRTL ? 'تم التحميل' : 'Loaded');
    } catch { toast.error(isRTL ? 'فشل التحميل' : 'Failed'); }
    finally { setLoading(false); }
  };

  const fmt = (n: number) => (n || 0).toFixed(3);
  const reportTitle = isRTL ? 'صرفيات المخزن للفروع' : 'Warehouse Dispatch to Branches';
  const compName = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;

  const filteredBranches = useMemo(() => {
    if (!result) return [];
    const term = searchTerm.toLowerCase();
    return result.branches
      .map(b => {
        const items = !searchTerm ? b.items : b.items.filter(i =>
          i.itemCode.toLowerCase().includes(term) ||
          i.itemName.toLowerCase().includes(term) ||
          i.itemNameAr.includes(searchTerm) ||
          b.branchName.toLowerCase().includes(term) ||
          b.branchNameAr.includes(searchTerm)
        );
        const branchTotalQuantity = items.reduce((s, i) => s + (i.totalQuantity || 0), 0);
        const branchTotalValue = items.reduce((s, i) => s + (i.totalValue || 0), 0);
        const branchPostedQty = items.reduce((s, i) => s + (i.postedQty || 0), 0);
        const branchDraftQty = items.reduce((s, i) => s + (i.draftQty || 0), 0);
        return { ...b, items, branchTotalQuantity, branchTotalValue, branchPostedQty, branchDraftQty };
      })
      .filter(b => b.items.length > 0);
  }, [result, searchTerm]);

  const filteredSummary = useMemo(() => {
    const uniqueItemIds = new Set<number>();
    let grandTotalQuantity = 0;
    let grandTotalValue = 0;
    filteredBranches.forEach(b => {
      grandTotalQuantity += b.branchTotalQuantity;
      grandTotalValue += b.branchTotalValue;
      b.items.forEach(i => uniqueItemIds.add(i.itemId));
    });
    return {
      totalBranches: filteredBranches.length,
      totalUniqueItems: uniqueItemIds.size,
      grandTotalQuantity,
      grandTotalValue,
    };
  }, [filteredBranches]);

  const exportToExcel = async () => {
    if (!result) return;
    try {
      const wb = new ExcelJS.Workbook(); const ws = wb.addWorksheet(reportTitle);
      if (companyInfo?.logoBase64) {
        try {
          const img = wb.addImage({ base64: companyInfo.logoBase64.split(',').pop() || companyInfo.logoBase64, extension: 'png' });
          ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 100, height: 80 } });
        } catch {}
      }
      ws.mergeCells('C1:G1'); ws.getCell('C1').value = compName || ''; ws.getCell('C1').font = { size: 18, bold: true, color: { argb: 'FF1565C0' } }; ws.getCell('C1').alignment = { horizontal: 'center' };
      ws.mergeCells('A3:G3'); const tc = ws.getCell('A3'); tc.value = `${reportTitle} — ${isRTL ? result.warehouseNameAr || result.warehouseName : result.warehouseName}`; tc.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } }; tc.alignment = { horizontal: 'center' }; tc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
      ws.mergeCells('A4:G4');
      ws.getCell('A4').value = `${isRTL ? 'من' : 'From'}: ${result.fromDate ? new Date(result.fromDate).toLocaleDateString('en-GB') : '-'}    ${isRTL ? 'إلى' : 'To'}: ${result.toDate ? new Date(result.toDate).toLocaleDateString('en-GB') : '-'}`;
      ws.getCell('A4').alignment = { horizontal: 'center' }; ws.getCell('A4').font = { italic: true, size: 11 };

      let row = 6;
      const headers = [isRTL ? 'كود' : 'Code', isRTL ? 'الصنف' : 'Item', isRTL ? 'الوحدة' : 'Unit', isRTL ? 'الكمية' : 'Qty', isRTL ? 'سعر الوحدة' : 'Unit Cost', isRTL ? 'القيمة' : 'Value', isRTL ? 'مسودة' : 'Draft'];

      filteredBranches.forEach(branch => {
        ws.mergeCells(`A${row}:G${row}`);
        const bc = ws.getCell(`A${row}`);
        bc.value = `${isRTL ? '🏢 الفرع' : '🏢 Branch'}: ${isRTL ? branch.branchNameAr || branch.branchName : branch.branchName}`;
        bc.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
        bc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E7D32' } };
        bc.alignment = { horizontal: isRTL ? 'right' : 'left' };
        row++;

        const hr = ws.getRow(row);
        headers.forEach((v, i) => {
          const c = hr.getCell(i + 1);
          c.value = v; c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
          c.alignment = { horizontal: 'center' };
        });
        row++;

        branch.items.forEach(item => {
          const r = ws.getRow(row);
          r.getCell(1).value = item.itemCode;
          r.getCell(2).value = isRTL ? item.itemNameAr : item.itemName;
          r.getCell(3).value = isRTL ? item.unitNameAr : item.unitName;
          r.getCell(4).value = item.totalQuantity;
          r.getCell(5).value = item.avgUnitCost;
          r.getCell(6).value = item.totalValue;
          r.getCell(7).value = item.draftQty > 0 ? `${fmt(item.draftQty)}` : '-';
          if (item.draftQty > 0) {
            r.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF3E0' } }; });
          }
          row++;
        });

        const tr = ws.getRow(row);
        tr.getCell(1).value = isRTL ? 'إجمالي الفرع' : 'Branch Total';
        tr.getCell(4).value = branch.branchTotalQuantity;
        tr.getCell(6).value = branch.branchTotalValue;
        tr.eachCell(c => { c.font = { bold: true }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } }; });
        row += 2;
      });

      ws.mergeCells(`A${row}:C${row}`);
      ws.getCell(`A${row}`).value = isRTL ? 'الإجمالي العام' : 'Grand Total';
      ws.getCell(`D${row}`).value = filteredSummary.grandTotalQuantity;
      ws.getCell(`F${row}`).value = filteredSummary.grandTotalValue;
      ws.getRow(row).eachCell(c => { c.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } }; });

      ws.columns = [{ width: 14 }, { width: 32 }, { width: 12 }, { width: 12 }, { width: 14 }, { width: 14 }, { width: 12 }];
      const buf = await wb.xlsx.writeBuffer(); const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `WarehouseDispatch_${Date.now()}.xlsx`; a.click();
      toast.success(isRTL ? 'تم التصدير' : 'Exported');
    } catch { toast.error(isRTL ? 'فشل' : 'Failed'); }
  };

  const exportToPdf = async () => {
    if (!result) return;
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pw = doc.internal.pageSize.width;
      if (companyInfo?.logoBase64) { try { doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25); } catch {} }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(21, 101, 192);
      doc.text(compName || '', pw / 2, 15, { align: 'center' });
      doc.setFillColor(21, 101, 192); doc.roundedRect(pw / 2 - 60, 32, 120, 10, 3, 3, 'F');
      doc.setFontSize(12); doc.setTextColor(255, 255, 255);
      doc.text(`${reportTitle} - ${result.warehouseName}`, pw / 2, 38.5, { align: 'center' });

      let startY = 50;
      filteredBranches.forEach(branch => {
        autoTable(doc, {
          head: [
            [{ content: `Branch: ${branch.branchName}`, colSpan: 6, styles: { fillColor: [46, 125, 50], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10 } }],
            ['Code', 'Item', 'Unit', 'Qty', 'Cost', 'Value']
          ],
          body: [
            ...branch.items.map(i => [i.itemCode, i.itemName, i.unitName, fmt(i.totalQuantity), fmt(i.avgUnitCost), fmt(i.totalValue)]),
            [{ content: 'Branch Total', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [227, 242, 253] } }, fmt(branch.branchTotalQuantity), '', fmt(branch.branchTotalValue)]
          ],
          startY,
          theme: 'grid',
          headStyles: { fillColor: [21, 101, 192], textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold', halign: 'center' },
          bodyStyles: { fontSize: 7 },
        });
        startY = (doc as any).lastAutoTable.finalY + 5;
      });

      autoTable(doc, {
        body: [[{ content: `Grand Total: Qty ${fmt(filteredSummary.grandTotalQuantity)}  /  Value ${fmt(filteredSummary.grandTotalValue)}`, styles: { fillColor: [21, 101, 192], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 11, halign: 'center' } }]],
        startY,
        theme: 'plain',
      });

      doc.save(`WarehouseDispatch_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير' : 'Exported');
    } catch { toast.error(isRTL ? 'فشل' : 'Failed'); }
  };

  const handlePrint = () => {
    if (!result) return;
    const w = window.open('', '_blank', 'width=1100,height=700'); if (!w) return;
    const branchesHtml = filteredBranches.map(b => `
      <div style="margin-top:14px">
        <div style="background:#2E7D32;color:white;padding:6px 10px;font-weight:bold;font-size:12px">🏢 ${isRTL ? 'الفرع' : 'Branch'}: ${isRTL ? b.branchNameAr || b.branchName : b.branchName}</div>
        <table style="width:100%;border-collapse:collapse;font-size:10px;margin-top:4px">
          <thead><tr>
            <th>${isRTL ? 'كود' : 'Code'}</th><th>${isRTL ? 'الصنف' : 'Item'}</th><th>${isRTL ? 'الوحدة' : 'Unit'}</th>
            <th>${isRTL ? 'الكمية' : 'Qty'}</th><th>${isRTL ? 'سعر الوحدة' : 'Unit Cost'}</th><th>${isRTL ? 'القيمة' : 'Value'}</th>
          </tr></thead>
          <tbody>
            ${b.items.map(i => `<tr${i.draftQty > 0 ? ' style="background:#FFF3E0"' : ''}>
              <td>${i.itemCode}</td><td>${isRTL ? i.itemNameAr : i.itemName}</td><td>${isRTL ? i.unitNameAr : i.unitName}</td>
              <td><b>${fmt(i.totalQuantity)}</b></td><td>${fmt(i.avgUnitCost)}</td><td><b>${fmt(i.totalValue)}</b></td>
            </tr>`).join('')}
            <tr style="background:#E3F2FD;font-weight:bold">
              <td colspan="3">${isRTL ? 'إجمالي الفرع' : 'Branch Total'}</td>
              <td>${fmt(b.branchTotalQuantity)}</td><td></td><td>${fmt(b.branchTotalValue)}</td>
            </tr>
          </tbody>
        </table>
      </div>`).join('');

    w.document.write(`<html dir="${isRTL ? 'rtl' : 'ltr'}"><head><title>${reportTitle}</title><style>
      body{font-family:'Segoe UI','Tajawal',sans-serif;margin:20px;color:#333}
      .header{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #1565C0;padding-bottom:10px;margin-bottom:10px}
      .header img{max-height:60px} .header h1{color:#1565C0;margin:0;font-size:20px}
      th{background:#1565C0;color:white;padding:6px;text-align:${isRTL ? 'right' : 'left'};border:1px solid #1565C0}
      td{padding:5px 6px;border:1px solid #ddd}
      .grand{margin-top:14px;background:#1565C0;color:white;padding:10px;text-align:center;font-size:14px;font-weight:bold}
      @media print{body{margin:10px}}
    </style></head><body>
    <div class="header">
      <div>${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}"/>` : ''}</div>
      <div style="text-align:center;flex:1">
        <h1>${reportTitle}</h1>
        <div style="color:#666;font-size:11px">${compName || ''}</div>
        <div style="color:#1565C0;font-size:12px;font-weight:bold;margin-top:4px">${isRTL ? 'المخزن' : 'Warehouse'}: ${isRTL ? result.warehouseNameAr || result.warehouseName : result.warehouseName}</div>
        <div style="color:#666;font-size:10px;margin-top:2px">${isRTL ? 'من' : 'From'}: ${result.fromDate ? new Date(result.fromDate).toLocaleDateString('en-GB') : '-'} - ${isRTL ? 'إلى' : 'To'}: ${result.toDate ? new Date(result.toDate).toLocaleDateString('en-GB') : '-'}</div>
      </div>
      <div style="width:80px"></div>
    </div>
    ${branchesHtml}
    <div class="grand">${isRTL ? 'الإجمالي العام' : 'Grand Total'}: ${isRTL ? 'الكمية' : 'Qty'} ${fmt(filteredSummary.grandTotalQuantity)} / ${isRTL ? 'القيمة' : 'Value'} ${fmt(filteredSummary.grandTotalValue)}</div>
    <div style="text-align:center;margin-top:10px;color:#999;font-size:9px">${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>
    </body></html>`);
    w.document.close(); setTimeout(() => w.print(), 500);
  };

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {reportTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isRTL ? 'كل البضاعة الخارجة من المخزن إلى الفروع — مرحّلة + مسودة (متابعة لحظية)' : 'All goods dispatched from warehouse to branches — Posted + Draft (live)'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Autocomplete size="small" sx={{ minWidth: 220 }}
            options={[{ id: '' as number | '', label: isRTL ? 'كل المخازن' : 'All Warehouses' }, ...warehouses.map(w => ({ id: w.warehouseId as number | '', label: isRTL ? w.warehouseNameAr || w.warehouseName : w.warehouseName }))]}
            getOptionLabel={o => o.label || ''} isOptionEqualToValue={(o, v) => o.id === v.id}
            value={[{ id: '' as number | '', label: isRTL ? 'كل المخازن' : 'All Warehouses' }, ...warehouses.map(w => ({ id: w.warehouseId as number | '', label: isRTL ? w.warehouseNameAr || w.warehouseName : w.warehouseName }))].find(o => o.id === whFilter) || { id: '' as number | '', label: isRTL ? 'كل المخازن' : 'All Warehouses' }}
            onChange={(_, v) => setWhFilter(v?.id || '')}
            renderInput={params => <TextField {...params} label={isRTL ? 'المخزن المصدر' : 'Source Warehouse'} />} />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker label={isRTL ? 'من تاريخ' : 'From'} value={fromDate} onChange={d => setFromDate(d)}
              format="dd/MM/yyyy" minDate={minDate} maxDate={toDate || maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
            <DatePicker label={isRTL ? 'إلى تاريخ' : 'To'} value={toDate} onChange={d => setToDate(d)}
              format="dd/MM/yyyy" minDate={fromDate || minDate} maxDate={maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
          </LocalizationProvider>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={loadReport} disabled={loading}
            sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', px: 3, borderRadius: 3 }}>
            {loading ? (isRTL ? 'جاري...' : 'Loading...') : (isRTL ? 'عرض' : 'Load')}
          </Button>
          {result && (<>
            <Button variant="contained" size="small" startIcon={<ExcelIcon />} onClick={exportToExcel} sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)', color: 'white', borderRadius: 3 }}>Excel</Button>
            <Button variant="contained" size="small" startIcon={<PdfIcon />} onClick={exportToPdf} sx={{ background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)', color: 'white', borderRadius: 3 }}>PDF</Button>
            <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)', color: 'white', borderRadius: 3 }}>{isRTL ? 'طباعة' : 'Print'}</Button>
          </>)}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControlLabel control={<Checkbox checked={includePosted} onChange={e => setIncludePosted(e.target.checked)} />}
          label={isRTL ? 'تشمل المرحّلة' : 'Include Posted'} />
        <FormControlLabel control={<Checkbox checked={includeDraft} onChange={e => setIncludeDraft(e.target.checked)} />}
          label={isRTL ? 'تشمل المسودات' : 'Include Drafts'} />
      </Box>

      {result && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent><Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'الفروع' : 'Branches'}</Typography><Typography variant="h4" fontWeight="bold">{filteredSummary.totalBranches}</Typography></CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #2E7D32 0%, #66BB6A 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent><Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'أصناف فريدة' : 'Unique Items'}</Typography><Typography variant="h4" fontWeight="bold">{filteredSummary.totalUniqueItems}</Typography></CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #FF8C00 0%, #FFA500 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent><Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'إجمالي الكمية' : 'Total Qty'}</Typography><Typography variant="h5" fontWeight="bold">{fmt(filteredSummary.grandTotalQuantity)}</Typography></CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #C62828 0%, #EF5350 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent><Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'القيمة الإجمالية' : 'Total Value'}</Typography><Typography variant="h5" fontWeight="bold">{fmt(filteredSummary.grandTotalValue)}</Typography></CardContent>
            </Card>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" placeholder={isRTL ? 'بحث بالصنف أو الفرع...' : 'Search item or branch...'} value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              sx={{ minWidth: 320 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
            <Typography variant="body2" color="text.secondary">
              {isRTL ? `${filteredBranches.length} فرع` : `${filteredBranches.length} branches`}
            </Typography>
          </Box>

          {filteredBranches.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
              <Typography color="text.secondary">{isRTL ? 'لا توجد بيانات' : 'No data'}</Typography>
            </Paper>
          ) : (
            filteredBranches.map((branch) => (
              <Paper key={branch.branchId} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ background: 'linear-gradient(90deg, #2E7D32 0%, #66BB6A 100%)', color: 'white', p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    🏢 {isRTL ? 'الفرع' : 'Branch'}: {isRTL ? branch.branchNameAr || branch.branchName : branch.branchName}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {branch.branchDraftQty > 0 && (
                      <Chip size="small" label={`${isRTL ? 'مسودة' : 'Draft'}: ${fmt(branch.branchDraftQty)}`}
                        sx={{ bgcolor: '#FFA726', color: 'white', fontWeight: 'bold' }} />
                    )}
                    <Chip size="small" label={`${isRTL ? 'الكمية' : 'Qty'}: ${fmt(branch.branchTotalQuantity)}`}
                      sx={{ bgcolor: 'white', color: '#2E7D32', fontWeight: 'bold' }} />
                    <Chip size="small" label={`${isRTL ? 'القيمة' : 'Value'}: ${fmt(branch.branchTotalValue)}`}
                      sx={{ bgcolor: 'white', color: '#2E7D32', fontWeight: 'bold' }} />
                  </Box>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'كود' : 'Code'}</TableCell>
                        <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                        <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                        <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الكمية' : 'Qty'}</TableCell>
                        <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'مرحّل' : 'Posted'}</TableCell>
                        <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'مسودة' : 'Draft'}</TableCell>
                        <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'سعر الوحدة' : 'Unit Cost'}</TableCell>
                        <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'القيمة' : 'Value'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {branch.items.map((item, idx) => (
                        <TableRow key={idx} sx={{ bgcolor: item.draftQty > 0 ? (isDark ? 'rgba(255,167,38,0.12)' : '#FFF8E1') : 'inherit', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' } }}>
                          <TableCell>{item.itemCode}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{isRTL ? item.itemNameAr : item.itemName}</Typography>
                          </TableCell>
                          <TableCell>{isRTL ? item.unitNameAr : item.unitName}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565C0' }}>{fmt(item.totalQuantity)}</TableCell>
                          <TableCell align="right" sx={{ color: '#2E7D32' }}>{fmt(item.postedQty)}</TableCell>
                          <TableCell align="right" sx={{ color: item.draftQty > 0 ? '#E65100' : 'inherit', fontWeight: item.draftQty > 0 ? 'bold' : 'normal' }}>
                            {item.draftQty > 0 ? fmt(item.draftQty) : '-'}
                          </TableCell>
                          <TableCell align="right">{fmtUnitPrice(item.avgUnitCost)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(item.totalValue)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: '#E3F2FD' }}>
                        <TableCell colSpan={3} sx={{ fontWeight: 'bold', color: '#0D47A1' }}>{isRTL ? 'إجمالي الفرع' : 'Branch Total'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565C0' }}>{fmt(branch.branchTotalQuantity)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>{fmt(branch.branchPostedQty)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#E65100' }}>{branch.branchDraftQty > 0 ? fmt(branch.branchDraftQty) : '-'}</TableCell>
                        <TableCell></TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#0D47A1' }}>{fmt(branch.branchTotalValue)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))
          )}

          {filteredBranches.length > 0 && (
            <Paper sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', color: 'white', p: 2, borderRadius: 3, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight="bold">
                {isRTL ? 'الإجمالي العام' : 'Grand Total'}: {isRTL ? 'الكمية' : 'Qty'} {fmt(filteredSummary.grandTotalQuantity)} / {isRTL ? 'القيمة' : 'Value'} {fmt(filteredSummary.grandTotalValue)}
              </Typography>
            </Paper>
          )}
        </>
      )}

      {!result && !loading && (
        <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
          <LocalShipping sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">{isRTL ? 'اختر المخزن والتاريخ ثم اضغط "عرض"' : 'Select warehouse and dates, then click "Load"'}</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default WarehouseDispatchReport;
